import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { fetchPublicInspection, submitPublicInspection, finishPublicInspectionAction, updatePublicInspectionActionDue, savePublicInspectionDraft, startPublicInspection } from '../services/publicPagesApi';
import { InspectionDetail } from '../services/inspectionsApi';
import InspectionForm, { Answers, fillContactFields } from './inspections/InspectionForm';
import DeviationChoice, { DeviationState, emptyDeviation } from './inspections/DeviationChoice';
import ActionPanel from './inspections/ActionPanel';
import { evaluateLive } from './inspections/evaluate';
import { fmtDate as fmtDateShared, fmtDateTime } from '../utils/format';
import { publicBodyStyle, publicCardStyle } from './shared/PublicPage';

/**
 * Egenkontroll via länk, utan inloggning. Länken (token) är nyckeln, som för avvikelserapporten.
 *
 * När en grupp är ansvarig får alla i gruppen var sin länk med ?c=<kontakt-id>. Den som
 * trycker "Påbörja kontrollen" äger den tills den är klar; de andra ser vem som håller på
 * och kan ta över om det behövs (t.ex. vid sjukdom).
 */

const bodyStyle: React.CSSProperties = { ...publicBodyStyle, padding: '16px' };
const cardStyle: React.CSSProperties = { ...publicCardStyle(640), padding: '24px' };
const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6,
    padding: '9px 10px', fontSize: 15, fontFamily: 'inherit',
};
const btnStyle: React.CSSProperties = {
    background: '#2563eb', color: 'white', border: 'none', borderRadius: 6,
    padding: '12px 24px', fontSize: 16, cursor: 'pointer', fontFamily: 'inherit', width: '100%',
};
const infoBox: React.CSSProperties = { background: '#e8f0fe', color: '#1a4aa6', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 14 };

const fmtDate = (d: string | null) => fmtDateShared(d, { month: 'long' });

const InspectionPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [searchParams] = useSearchParams();
    // ?c=<kontakt-id> i länken: vem som öppnat sidan. Skickas med i alla anrop.
    const contactId = useMemo(() => { const n = Number(searchParams.get('c')); return Number.isInteger(n) && n > 0 ? n : null; }, [searchParams]);

    const [data, setData] = useState<InspectionDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [answers, setAnswers] = useState<Answers>({});
    const [name, setName] = useState(() => { try { return localStorage.getItem('inspection_name') ?? ''; } catch { return ''; } });
    const [deviation, setDeviation] = useState<DeviationState>(emptyDeviation());
    const [note, setNote] = useState('');
    const [deviationKeys, setDeviationKeys] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [starting, setStarting] = useState(false);
    const [draftSaved, setDraftSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;
        fetchPublicInspection(token, contactId)
            .then(d => {
                // Namnet från länken vinner över det som sparats i webbläsaren.
                const initialName = d.viewer?.name || name;
                setName(initialName);
                setData(d); setAnswers(fillContactFields(d.fields, d.answers ?? {}, initialName)); setNote(d.note ?? '');
            })
            .catch(err => { if (err?.response?.status === 404) setNotFound(true); else setError('Kunde inte hämta kontrollen.'); })
            .finally(() => setLoading(false));
        // Namnet läses bara när kontrollen laddas; ändringar efteråt hanteras av changeName.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, contactId]);

    const done = data && data.status !== 'pending' && data.status !== 'overdue';
    // Någon annan i gruppen håller på med kontrollen.
    const lockedByOther = !!data && !done && !!data.locked;
    // Gruppkontroll som ingen påbörjat ännu: visa "Påbörja"-steget före formuläret.
    const needsStart = !!data && !done && !lockedByOther && data.group_owned && !data.started_at;
    const startedByMe = !!data && !done && !!data.started_at && !data.locked && (data.group_owned || !!data.viewer);

    // Namnet i namnrutan följer med till kontaktfälten så länge de inte ändrats för hand.
    const changeName = (next: string) => {
        if (data && !done) setAnswers(prev => fillContactFields(data.fields, prev, next, name));
        setName(next);
    };
    const liveKeys = useMemo(() => (data && !done ? evaluateLive(data.fields, answers).deviationKeys : []), [data, done, answers]);
    const shownKeys = done ? deviationKeys : liveKeys;

    const rememberName = (n: string) => { try { localStorage.setItem('inspection_name', n); } catch { /* ignore */ } };

    /** Fel från servern. 409 = någon annan hann före; ladda om så låset syns. */
    const showApiError = async (err: any, fallback: string) => {
        const d = err?.response?.data;
        setError(d?.error || fallback);
        if (err?.response?.status === 409 && token) {
            try { setData(await fetchPublicInspection(token, contactId)); } catch { /* behåll det vi har */ }
        }
        return d;
    };

    /** Påbörjar kontrollen, eller tar över den från någon annan. */
    const start = async (takeover: boolean) => {
        if (!name.trim()) { setError('Skriv ditt namn'); return; }
        if (takeover && !window.confirm(`${data?.started_by_name ?? 'Någon annan'} har redan påbörjat kontrollen. Vill du ta över den?`)) return;
        setStarting(true); setError(null);
        try {
            rememberName(name.trim());
            const d = await startPublicInspection(token!, { name: name.trim(), contact_id: contactId, takeover });
            setData(d);
            setAnswers(fillContactFields(d.fields, d.answers ?? {}, name.trim()));
            setNote(d.note ?? '');
        } catch (err: any) {
            await showApiError(err, 'Kunde inte påbörja kontrollen. Försök igen.');
        } finally { setStarting(false); }
    };

    const finishAction = async (noteText: string, byName: string) => {
        rememberName(byName);
        setData(await finishPublicInspectionAction(token!, { action_done_note: noteText, action_done_by_name: byName, contact_id: contactId }));
        window.scrollTo({ top: 0 });
    };

    /** Sparar det som är ifyllt så långt; kontrollen ligger kvar som påbörjad. */
    const saveDraft = async () => {
        setSavingDraft(true); setError(null); setDraftSaved(false);
        try {
            if (name.trim()) rememberName(name.trim());
            setData(await savePublicInspectionDraft(token!, { answers, note, name: name.trim(), contact_id: contactId }));
            setDraftSaved(true);
        } catch (err: any) {
            await showApiError(err, 'Kunde inte spara. Försök igen.');
        } finally { setSavingDraft(false); }
    };

    const changeActionDue = async (date: string) => {
        setData(await updatePublicInspectionActionDue(token!, date, contactId));
    };

    const submit = async (e: React.FormEvent | null, noNeed = false) => {
        e?.preventDefault();
        if (!name.trim()) { setError('Skriv ditt namn'); return; }
        setSaving(true); setError(null);
        try {
            rememberName(name.trim());
            const saved = await submitPublicInspection(token!, {
                answers, note, completed_by_name: name.trim(), contact_id: contactId, no_need: noNeed,
                deviation_mode: deviation.mode,
                deviation_note: deviation.deviationNote,
                action_note: deviation.actionNote,
                action_due_date: deviation.actionDue,
            });
            setData(saved);
            setDeviationKeys((saved.deviations ?? []).map(d => d.key));
            window.scrollTo({ top: 0 });
        } catch (err: any) {
            const d = await showApiError(err, 'Kunde inte spara. Försök igen.');
            if (Array.isArray(d?.deviations)) setDeviationKeys(d.deviations.map((x: any) => x.key));
        } finally { setSaving(false); }
    };

    if (loading) return <div style={bodyStyle}><div style={cardStyle}><p style={{ color: '#888' }}>Laddar…</p></div></div>;
    if (notFound || !data) {
        return (
            <div style={bodyStyle}><div style={cardStyle}>
                <h1 style={{ color: '#c0392b', fontSize: 22 }}>Länken hittades inte</h1>
                <p style={{ color: '#666' }}>Länken till egenkontrollen är ogiltig.</p>
            </div></div>
        );
    }

    const nameField = (
        <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 14 }}>Ditt namn *</label>
            <input value={name} onChange={e => changeName(e.target.value)} style={inputStyle} />
        </div>
    );
    const errorBox = error && <div style={{ background: '#f8d7da', color: '#721c24', padding: 10, borderRadius: 6, marginTop: 12 }}>{error}</div>;

    return (
        <div style={bodyStyle}>
            <div style={cardStyle}>
                {data.round_token && (
                    <div style={{ marginBottom: 10 }}>
                        <Link to={`/round/${data.round_token}${contactId ? `?c=${contactId}` : ''}`} style={{ fontSize: 14, color: '#1a4aa6' }}>
                            ← Tillbaka till ronden{data.round_total ? ` (${data.round_done ?? 0} av ${data.round_total} platser gjorda)` : ''}
                        </Link>
                    </div>
                )}
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Egenkontroll</div>
                <h1 style={{ margin: '4px 0 4px', fontSize: 22 }}>{data.schedule_name ?? data.template_name}{data.zone_name && data.round_token ? ` · ${data.zone_name}` : ''}</h1>
                <div style={{ color: '#666', fontSize: 14, marginBottom: 18 }}>
                    {data.template_name}{data.zone_name ? ` · ${data.zone_name}` : ''} · ska vara gjord senast {fmtDate(data.due_date)}
                </div>

                {done ? (
                    <>
                        <div style={{ background: data.has_deviation ? '#fff3cd' : '#d1e7dd', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 15 }}>
                            {data.status === 'skipped'
                                ? <>Kontrollen hoppades över. {data.note}</>
                                : data.no_need
                                    ? <>Tack! Sparat som <b>utfört, inget behov</b>{data.completed_by_name ? ` av ${data.completed_by_name}` : ''}.</>
                                    : <>Tack! Kontrollen är sparad{data.completed_by_name ? ` av ${data.completed_by_name}` : ''}.{data.has_deviation && data.deviation_note && <> Något var inte i ordning: <b>{data.deviation_note}</b></>}</>}
                        </div>
                        <ActionPanel inspection={data} onDone={finishAction} onChangeDue={changeActionDue} askName defaultName={name} />
                        <div style={{ height: 12 }} />
                        <InspectionForm fields={data.fields} answers={answers} readOnly deviationKeys={shownKeys} />
                    </>
                ) : lockedByOther ? (
                    <>
                        <div style={{ background: '#fff3cd', color: '#664d03', borderRadius: 8, padding: 14, fontSize: 15 }}>
                            <b>{data.started_by_name ?? 'Någon annan'}</b> påbörjade kontrollen{data.started_at ? ` ${fmtDateTime(data.started_at)}` : ''} och gör klart den.
                            <div style={{ fontSize: 14, marginTop: 6 }}>Du behöver inte göra något. Behöver du ändå ta över, t.ex. för att hen inte kan göra klart, fyll i ditt namn och tryck på knappen.</div>
                        </div>
                        {nameField}
                        {errorBox}
                        <button type="button" onClick={() => start(true)} disabled={starting}
                            style={{ ...btnStyle, marginTop: 14, background: '#fff', color: '#664d03', border: '1px solid #ffc107' }}>
                            {starting ? 'Tar över…' : 'Ta över kontrollen'}
                        </button>
                    </>
                ) : needsStart ? (
                    <>
                        <div style={infoBox}>
                            Kontrollen har gått till alla i gruppen <b>{data.responsible_group_name ?? 'gruppen'}</b>. Den som påbörjar den gör klart den, och de andra ser vem som håller på.
                        </div>
                        {nameField}
                        {errorBox}
                        <button type="button" onClick={() => start(false)} disabled={starting} style={{ ...btnStyle, marginTop: 18 }}>
                            {starting ? 'Påbörjar…' : 'Påbörja kontrollen'}
                        </button>
                    </>
                ) : (
                    <form onSubmit={submit}>
                        {startedByMe && !draftSaved && (
                            <div style={infoBox}>
                                Du påbörjade kontrollen{data.started_at ? ` ${fmtDateTime(data.started_at)}` : ''}.{data.group_owned ? ' De andra i gruppen ser att du håller på.' : ''}{data.answers && Object.keys(data.answers).length > 0 ? ' Fortsätt där du slutade.' : ''}
                            </div>
                        )}
                        {!startedByMe && data.has_draft && !draftSaved && (
                            <div style={infoBox}>
                                Kontrollen är påbörjad{data.started_by_name ? ` av ${data.started_by_name}` : ''}. Fortsätt där du slutade.
                            </div>
                        )}
                        <InspectionForm fields={data.fields} answers={answers} prefill={data.prefill} deviationKeys={shownKeys}
                            onChange={(k, v) => setAnswers(prev => ({ ...prev, [k]: v }))} />

                        {shownKeys.length > 0 && <DeviationChoice value={deviation} onChange={setDeviation} />}
                        <div style={{ marginTop: 12 }}>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 14 }}>Kommentar (valfritt)</label>
                            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={inputStyle} />
                        </div>
                        {nameField}
                        {errorBox}
                        <button type="submit" disabled={saving || savingDraft} style={{ ...btnStyle, marginTop: 18 }}>{saving ? 'Sparar…' : 'Spara kontrollen'}</button>
                        <button type="button" onClick={() => submit(null, true)} disabled={saving || savingDraft}
                            style={{ ...btnStyle, marginTop: 10, background: '#20c997' }}>
                            Utfört, inget behov
                        </button>
                        <div style={{ color: '#888', fontSize: 12, marginTop: 4, textAlign: 'center' }}>Var du där och inget behövde göras? Tryck "Utfört, inget behov", inga fält krävs.</div>
                        <button type="button" onClick={saveDraft} disabled={saving || savingDraft}
                            style={{ ...btnStyle, marginTop: 10, background: '#e8f0fe', color: '#1a4aa6' }}>
                            {savingDraft ? 'Sparar…' : 'Spara och fortsätt senare'}
                        </button>
                        {draftSaved && <div style={{ color: '#28a745', fontSize: 14, marginTop: 10, textAlign: 'center' }}>Utkastet är sparat. Du kan stänga sidan och fortsätta senare med samma länk.</div>}
                        <div style={{ color: '#888', fontSize: 12, marginTop: 8, textAlign: 'center' }}>Kontrollen räknas som gjord först när du trycker Spara kontrollen.</div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default InspectionPage;
